/**
 * ParachPay App Entry Point
 * Startup isolation: each non-essential step wrapped in try/catch
 * Critical for Huawei WebView compatibility
 */
import React from "react";
import ReactDOM from "react-dom/client";

// CRITICAL: Import CSS early, but wrap in case of issues
try {
  require("@/index.css");
} catch (e) {
  console.error('[Startup] CSS import failed:', e);
}

import App from "@/App";
import { Capacitor } from "@capacitor/core";

// Safe storage validation on boot
const validateStoredValues = () => {
  const VALID_LANGUAGES = [
    'en-US', 'en-GB', 'af', 'ar', 'da', 'de', 'el', 'es', 'fr', 'hi', 'id', 'it',
    'ja', 'ko', 'ms', 'nl', 'pl', 'pt-BR', 'pt-PT', 'ro', 'ru', 'sk', 'sv', 'tr',
    'vi', 'zh-CN', 'zh-TW'
  ];
  
  try {
    // Validate language
    const lang = localStorage.getItem('parachpay_language');
    if (lang && !VALID_LANGUAGES.includes(lang)) {
      console.warn('[Startup] Invalid stored language, removing:', lang);
      localStorage.removeItem('parachpay_language');
    }
    
    // Validate theme
    const theme = localStorage.getItem('parachpay_theme');
    if (theme && !['light', 'dark'].includes(theme)) {
      console.warn('[Startup] Invalid stored theme, removing:', theme);
      localStorage.removeItem('parachpay_theme');
    }
    
    // Validate any JSON stored values
    const jsonKeys = ['parachpay_user', 'parachpay_session'];
    for (const key of jsonKeys) {
      const value = localStorage.getItem(key);
      if (value) {
        try {
          JSON.parse(value);
        } catch {
          console.warn('[Startup] Invalid JSON in storage, removing:', key);
          localStorage.removeItem(key);
        }
      }
    }
  } catch (e) {
    console.warn('[Startup] Storage validation failed:', e);
  }
};

// Safe PWA initialization (DISABLED inside native Capacitor app)
const initPWASafe = () => {
  try {
    // CRITICAL: Do NOT register service worker inside native app
    // This causes conflicts with Capacitor's WebView
    const isNative = Capacitor.isNativePlatform();
    if (isNative) {
      console.log('[PWA] Skipping - running in native Capacitor app');
      return;
    }
    
    // Only initialize PWA for web browsers
    const { initPWA } = require("@/lib/pwa");
    initPWA();
  } catch (e) {
    console.warn('[PWA] Init failed (non-critical):', e);
  }
};

// Safe Capacitor back button handler
const initCapacitorSafe = () => {
  try {
    if (!Capacitor.isNativePlatform()) return;
    
    const { App: CapacitorApp } = require("@capacitor/app");
    CapacitorApp.addListener('backButton', () => {
      try {
        const path = window.location.pathname;
        
        // If on home, dashboard, or /home, minimize app instead of going back to blank
        if (path === '/' || path === '/dashboard' || path === '/home') {
          CapacitorApp.minimizeApp();
        } else {
          // Safe to go back on other pages
          window.history.back();
        }
      } catch (e) {
        console.warn('[Capacitor] Back button handler error:', e);
      }
    });
  } catch (e) {
    console.warn('[Capacitor] Init failed (non-critical):', e);
  }
};

// ============ STARTUP SEQUENCE ============

// Step 1: Validate stored values (before anything reads them)
validateStoredValues();

// Step 2: Initialize PWA (web only, not native)
initPWASafe();

// Step 3: Initialize Capacitor handlers
initCapacitorSafe();

// Step 4: Render the app
try {
  const rootElement = document.getElementById("root");
  if (!rootElement) {
    throw new Error("Root element not found");
  }
  
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
} catch (e) {
  console.error('[Startup] Fatal render error:', e);
  
  // Fallback: show basic error message
  const rootElement = document.getElementById("root");
  if (rootElement) {
    rootElement.innerHTML = `
      <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:#1a1a2e;color:white;padding:20px;text-align:center;font-family:system-ui,sans-serif;">
        <div>
          <div style="font-size:48px;margin-bottom:16px;">P</div>
          <h1 style="font-size:24px;margin-bottom:8px;">ParachPay</h1>
          <p style="color:#94a3b8;margin-bottom:16px;">Unable to load the app. Please reload or try again later.</p>
          <button onclick="window.location.reload()" style="background:#10b981;color:white;padding:12px 24px;border:none;border-radius:8px;cursor:pointer;">
            Reload App
          </button>
        </div>
      </div>
    `;
  }
  
  // Report the error
  try {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client-errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: 'Fatal startup error: ' + e.message,
        stack: e.stack,
        url: window.location.href,
        version: '1.1.9',
        ua: navigator.userAgent,
        lang: navigator.language
      })
    }).catch(() => {});
  } catch {}
}
